import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, Loader2, Search, Ban, CheckCircle2, X, Wallet, TrendingUp,
  ArrowUpCircle, Plus, Eye, UserPlus, Info, Trash2, Percent, Undo2,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';
import SettingsPage from './Settings';

const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-');

const TYPE_LABELS = {
  revshare: 'Net Yatırım Payı', deposit: 'Yatırım %', cpa: 'CPA', manual: 'Manuel',
};

// Bakiye düzeltmesinin yönü tutarın işaretinden değil ayrı bir seçimden gelir;
// tutar her zaman pozitif okunur (yanlışlıkla yazılan eksi işareti yönü
// değiştirmez, seçim değiştirir).
const adjustDirection = (form) => (form?.direction === 'subtract' ? 'subtract' : 'add');

const adjustAmount = (form) => {
  const value = Number(String(form?.delta ?? '').replace(',', '.').trim());
  if (!Number.isFinite(value)) return 0;
  return Math.round(Math.abs(value) * 100) / 100;
};

const adjustDelta = (form) => {
  const amount = adjustAmount(form);
  return adjustDirection(form) === 'subtract' ? -amount : amount;
};

// Elle yapılan düşüm bakiyeyi eksiye indiremez (backend de reddeder); sınır
// kaydetmeden önce ekranda görünmelidir.
const adjustOverdraft = (form) => {
  const delta = adjustDelta(form);
  return delta < 0 && Math.abs(delta) > Number(form?.balance || 0) + 0.0001;
};

// Komisyon her ortak için ayrı tanımlanır; ortak (genel) bir oran yoktur.
const commissionText = (a) => {
  const type = a.commission_type || 'revshare';
  if (type === 'cpa') {
    const amount = Number(a.cpa_amount || 0);
    if (!amount) return null;
    const min = Number(a.cpa_min_deposit || 0);
    return `CPA ${money(amount)}${min ? ` · min. ${money(min)}` : ''}`;
  }
  const percent = Number(type === 'deposit' ? a.deposit_percent : a.revshare_percent) || 0;
  if (!percent) return null;
  return `${TYPE_LABELS[type]} %${percent}`;
};

const emptyCommission = () => ({
  commission_type: 'revshare',
  revshare_percent: '',
  deposit_percent: '',
  cpa_amount: '',
  cpa_min_deposit: '',
});

const commissionPayload = (form) => {
  const kind = form.commission_type;
  const field = kind === 'cpa' ? 'cpa_amount'
    : kind === 'deposit' ? 'deposit_percent' : 'revshare_percent';
  const amount = Number(form[field]);
  if (form[field] === '' || !Number.isFinite(amount) || amount < 0) return null;
  if (kind !== 'cpa' && amount > 100) return null;

  const payload = { commission_type: kind, [field]: amount };
  if (kind === 'cpa') {
    const minimum = Number(form.cpa_min_deposit || 0);
    if (!Number.isFinite(minimum) || minimum < 0) return null;
    payload.cpa_min_deposit = minimum;
  }
  return payload;
};

const CommissionFields = ({ form, onChange }) => (
  <>
    <div>
      <label className="text-xs text-slate-400 block mb-1">Komisyon Modeli</label>
      <select
        value={form.commission_type}
        onChange={(e) => onChange({ ...form, commission_type: e.target.value })}
        className="admin-input"
      >
        <option value="revshare">Gelir Payı — net kayıptan %</option>
        <option value="deposit">Yatırım Yüzdesi — onaylı yatırımdan %</option>
        <option value="cpa">CPA — ilk yatırım başına sabit tutar</option>
      </select>
    </div>
    {form.commission_type === 'revshare' && (
      <div>
        <label className="text-xs text-slate-400 block mb-1">Gelir Payı Oranı (%)</label>
        <input
          type="number" step="0.1" min="0" max="100"
          value={form.revshare_percent}
          onChange={(e) => onChange({ ...form, revshare_percent: e.target.value })}
          className="admin-input" placeholder="Örn. 25"
        />
      </div>
    )}
    {form.commission_type === 'deposit' && (
      <div>
        <label className="text-xs text-slate-400 block mb-1">Yatırım Yüzdesi (%)</label>
        <input
          type="number" step="0.1" min="0" max="100"
          value={form.deposit_percent}
          onChange={(e) => onChange({ ...form, deposit_percent: e.target.value })}
          className="admin-input" placeholder="Örn. 5"
        />
      </div>
    )}
    {form.commission_type === 'cpa' && (
      <>
        <div>
          <label className="text-xs text-slate-400 block mb-1">CPA Tutarı (₺)</label>
          <input
            type="number" step="1" min="0" value={form.cpa_amount}
            onChange={(e) => onChange({ ...form, cpa_amount: e.target.value })}
            className="admin-input" placeholder="Örn. 250"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">CPA için Min. İlk Yatırım (₺)</label>
          <input
            type="number" step="1" min="0" value={form.cpa_min_deposit}
            onChange={(e) => onChange({ ...form, cpa_min_deposit: e.target.value })}
            className="admin-input" placeholder="Örn. 500"
          />
        </div>
      </>
    )}
  </>
);

const Affiliates = () => {
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  // Programın genel kuralları (Ayarlar → Affiliate sekmesi) bu sayfanın
  // üçüncü sekmesidir.
  const showProgram = can('content.read');
  // Çekim talebini sonuçlandırmak para ödemesidir: affiliate yönetimi değil,
  // finans yetkisi ister (bkz. affiliate_router.a_resolve_withdraw).
  const canPayout = can('finance.write');
  const canManage = can('affiliate.write');
  const [tab, setTab] = useState('affiliates');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [withdraws, setWithdraws] = useState([]);
  const [wStatus, setWStatus] = useState('beklemede');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [creating, setCreating] = useState(null);
  const [editCommission, setEditCommission] = useState(null);
  const [addMember, setAddMember] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (tab === 'program') return;
    setLoading(true);
    try {
      if (tab === 'affiliates') {
        const { data } = await client.get('/admin/affiliate/list', { params: { q: q || undefined } });
        setItems(data.items || []);
        setTotals(data.totals || {});
      } else {
        const { data } = await client.get('/admin/affiliate/withdraws/list', { params: { status: wStatus } });
        setWithdraws(data.items || []);
      }
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Veriler yüklenemedi' });
    } finally {
      setLoading(false);
    }
  }, [tab, q, wStatus, toast]);

  useEffect(() => { load(); }, [load]);
  // Klasik görünüme dönülürse sekmesi olmayan bir ekranda kalınmasın.
  useEffect(() => {
    if (tab === 'program' && !showProgram) setTab('affiliates');
  }, [tab, showProgram]);

  const openDetail = async (a) => {
    try {
      const { data } = await client.get(`/admin/affiliate/${a.id}`);
      setDetail(data);
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Detay alınamadı' });
    }
  };

  const toggleStatus = async (a) => {
    const next = a.status === 'active' ? 'suspended' : 'active';
    try {
      await client.patch(`/admin/affiliate/${a.id}`, { status: next });
      toast({ title: next === 'active' ? 'Aktifleştirildi' : 'Askıya alındı' });
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  // Ortaklığın kendisini kaldırır: kayıt silinir ve üyenin affiliate yetkisi
  // geri alınır. Hareketi olan ortaklar için backend 409 döner.
  const removeAffiliate = async (a) => {
    if (!window.confirm(
      `${a.username} ortaklığı tamamen kaldırılsın mı?\n\n`
      + 'Referans kodu iptal edilir ve üyenin affiliate yetkisi geri alınır. '
      + 'Komisyonu veya referansı olan ortaklar silinemez.',
    )) return;
    try {
      await client.delete(`/admin/affiliate/${a.id}`);
      toast({ title: 'Ortaklık kaldırıldı', description: `${a.username} artık ortak değil.` });
      load();
    } catch (e) {
      toast({ title: 'Kaldırılamadı', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  // Manuel düzeltme silinmez, ters kayıtla geri alınır: komisyon geçmişi bir
  // denetim izidir ve yanlış yazılan tutarın izi de kalmalıdır.
  const revertCommission = async (c) => {
    const amount = Math.round(Number(c.amount || 0) * 100) / 100;
    if (!amount || !detail?.affiliate?.id) return;
    if (!window.confirm(
      `${money(amount)} tutarındaki manuel düzeltme geri alınsın mı?\n\n`
      + 'Kayıt silinmez: ters yönde ikinci bir düzeltme yazılır ve bakiye '
      + 'düzeltmeden önceki hâline döner.',
    )) return;
    setBusy(true);
    try {
      await client.patch(`/admin/affiliate/${detail.affiliate.id}`, {
        balance_delta: -amount,
        note: `Manuel düzeltme geri alındı (${money(amount)})`,
      });
      toast({ title: 'Düzeltme geri alındı' });
      await openDetail(detail.affiliate);
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const submitAdjust = async () => {
    const delta = adjustDelta(adjust);
    if (!delta) {
      toast({ title: 'Geçersiz tutar', description: 'Sıfırdan büyük bir tutar girin' });
      return;
    }
    if (adjustOverdraft(adjust)) {
      toast({
        title: 'Bakiye yetersiz',
        description: `En fazla ${money(adjust.balance)} düşebilirsiniz`,
      });
      return;
    }
    setBusy(true);
    try {
      await client.patch(`/admin/affiliate/${adjust.id}`, {
        balance_delta: delta, note: adjust.note,
      });
      toast({
        title: delta < 0 ? 'Bakiyeden düşüldü' : 'Bakiyeye eklendi',
        description: `${adjust.username} · ${money(Math.abs(delta))}`,
      });
      setAdjust(null);
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = async () => {
    const username = (creating.username || '').trim();
    if (!username) {
      toast({ title: 'Eksik bilgi', description: 'Kullanıcı adı girin' });
      return;
    }
    const commission = commissionPayload(creating);
    if (!commission) {
      toast({ title: 'Geçersiz komisyon', description: 'Komisyon oranını veya tutarını kontrol edin' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await client.post('/admin/affiliate', {
        username,
        payout_name: creating.payout_name || '',
        payout_iban: creating.payout_iban || '',
        grant_access: true,
        ...commission,
      });
      toast({
        title: data.created ? 'Ortak oluşturuldu' : 'Ortak zaten kayıtlı',
        description: `Referans kodu: ${data.affiliate.referral_code}`,
      });
      setCreating(null);
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const submitCommission = async () => {
    const payload = commissionPayload(editCommission);
    if (!payload) {
      toast({ title: 'Geçersiz komisyon', description: 'Komisyon oranını veya tutarını kontrol edin' });
      return;
    }
    setBusy(true);
    try {
      await client.patch(`/admin/affiliate/${editCommission.id}`, payload);
      toast({
        title: 'Komisyon oranı kaydedildi',
        description: `${editCommission.username} için yeni oran hemen geçerli.`,
      });
      setEditCommission(null);
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Komisyon kaydedilemedi' });
    } finally {
      setBusy(false);
    }
  };

  const submitAddMember = async () => {
    const username = (addMember.username || '').trim();
    if (!username) {
      toast({ title: 'Eksik bilgi', description: 'Kullanıcı adı girin' });
      return;
    }
    setBusy(true);
    try {
      await client.post(`/admin/affiliate/${addMember.id}/referrals`, { username });
      toast({
        title: 'Üye bağlandı',
        description: `${username} artık ${addMember.affiliateName} ortağının referansı.`,
      });
      setAddMember(null);
      if (detail?.affiliate?.id === addMember.id) await openDetail(detail.affiliate);
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const resolveWithdraw = async (w, status) => {
    const note = status === 'reddedildi'
      ? window.prompt('Red gerekçesi:', '') ?? ''
      : window.prompt('Not (opsiyonel):', 'Ödeme yapıldı') ?? '';
    try {
      await client.patch(`/admin/affiliate/withdraws/${w.id}`, { status, admin_note: note });
      toast({ title: status === 'onaylandi' ? 'Onaylandı' : 'Reddedildi' });
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.15)', color: 'var(--v2-accent-text, #a855f7)' }}>
          <Users size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Affiliate Yönetimi</h1>
          <p className="text-xs text-slate-400">Ortaklar, komisyonlar ve çekim talepleri</p>
        </div>
        <button
          onClick={() => setCreating({
            username: '', payout_name: '', payout_iban: '', ...emptyCommission(),
          })}
          className="admin-btn-primary ml-auto flex items-center gap-2"
        >
          <UserPlus size={14} /> Yeni Ortak Ekle
        </button>
      </div>

      <div className="admin-card mb-4 flex items-start gap-2 p-3 text-xs text-slate-400">
        <Info size={14} className="mt-0.5 shrink-0 text-cyan-400" />
        <span>
          Komisyon oranı kişiye özeldir. Yeni ortak oluştururken oranı birlikte
          kaydedebilir, mevcut ortağın oranını satırdaki yüzde düğmesinden
          güncelleyebilirsiniz.
        </span>
      </div>

      {/* İş ortaklarına panel hesabı açılmaz: kendi portalları vardır ve
          orada yalnızca kendi verilerini salt okunur görürler. */}
      <div className="admin-card mb-4 flex items-start gap-2 p-3 text-xs text-slate-400">
        <Info size={14} className="mt-0.5 shrink-0 text-emerald-400" />
        <span>
          İş ortakları sitenin <strong className="text-slate-200">/ortak</strong>
          {' '}adresinden kendi üye hesaplarıyla giriş yapar ve yalnızca kendi
          referans, bahis ve komisyon raporlarını görür. Ortağa yönetim paneli
          hesabı açmayın: panel hesabı diğer ortakları ve panelin işleyişini
          gösterir.
        </span>
      </div>

      {tab === 'affiliates' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="admin-card p-4">
            <div className="text-xs text-slate-400">Ortak Sayısı</div>
            <div className="text-2xl font-bold mt-1">{totals.count || 0}</div>
          </div>
          <div className="admin-card p-4">
            <div className="text-xs text-slate-400">Toplam Komisyon</div>
            <div className="text-2xl font-bold text-cyan-400 mt-1">{money(totals.total_earned)}</div>
          </div>
          <div className="admin-card p-4">
            <div className="text-xs text-slate-400">Çekilebilir Bakiye</div>
            <div className="text-2xl font-bold text-emerald-400 mt-1">{money(totals.total_balance)}</div>
          </div>
          <div className="admin-card p-4">
            <div className="text-xs text-slate-400">Bekleyen Çekim</div>
            <div className="text-2xl font-bold text-amber-400 mt-1">{money(totals.total_pending)}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <button onClick={() => setTab('affiliates')} className={`admin-tab ${tab === 'affiliates' ? 'active' : ''}`}>Ortaklar</button>
        <button onClick={() => setTab('withdraws')} className={`admin-tab ${tab === 'withdraws' ? 'active' : ''}`}>Çekim Talepleri</button>
        {showProgram && (
          <button onClick={() => setTab('program')} className={`admin-tab ${tab === 'program' ? 'active' : ''}`}>Program Ayarları</button>
        )}

        {tab === 'program' ? null : tab === 'affiliates' ? (
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} className="admin-input pl-10" placeholder="Kullanıcı adı veya kod ara..." />
          </div>
        ) : (
          <select value={wStatus} onChange={(e) => setWStatus(e.target.value)} className="admin-input ml-auto max-w-[200px]">
            <option value="beklemede">Beklemede</option>
            <option value="onaylandi">Onaylanan</option>
            <option value="reddedildi">Reddedilen</option>
            <option value="all">Tümü</option>
          </select>
        )}
      </div>

      {tab === 'program' ? (
        <SettingsPage
          onlyTabs={['affiliate']}
          title="Program Ayarları"
          desc="Ortaklık programının açılması ve genel kuralları. Komisyon oranı ortak başına girilir."
        />
      ) : loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : tab === 'affiliates' ? (
        <div className="admin-card overflow-x-auto">
          <table className="admin-table w-full min-w-[900px]">
            <thead>
              <tr>
                <th>ORTAK</th><th>KOD</th><th>KOMİSYON</th><th>TIKLAMA</th><th>KAYIT</th><th>YATIRIM YAPAN</th>
                <th>TOPLAM KAZANÇ</th><th>BAKİYE</th><th>DURUM</th><th>İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="font-semibold">{a.username}</td>
                  <td><span className="font-mono text-cyan-400">{a.referral_code}</span></td>
                  <td>
                    {commissionText(a)
                      ? <span className="text-slate-200 text-xs">{commissionText(a)}</span>
                      : <span className="badge-pill badge-yellow">Tanımsız</span>}
                  </td>
                  <td className="text-slate-400">{a.clicks || 0}</td>
                  <td className="text-slate-300">{a.signup_count || 0}</td>
                  <td className="text-emerald-400">{a.deposited_count || 0}</td>
                  <td className="font-bold text-cyan-400">{money(a.total_earned)}</td>
                  <td className="font-bold">{money(a.balance)}</td>
                  <td>
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`badge-pill ${a.status === 'active' ? 'badge-green' : 'badge-gray'}`}>
                        {a.status === 'active' ? 'Aktif' : 'Askıda'}
                      </span>
                      {/* Yetkisi alınmış ortağın kodu artık çalışmaz; kaydın
                          neden hâlâ listede olduğu burada görünür. */}
                      {!a.can_use_affiliate && (
                        <span className="badge-pill badge-red" title="Üyenin affiliate yetkisi kaldırılmış; referans kodu çalışmıyor">
                          Yetki yok
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => openDetail(a)} className="icon-btn view" title="Detay"><Eye size={13} /></button>
                      <button
                        onClick={() => setAddMember({ id: a.id, affiliateName: a.username, username: '' })}
                        className="icon-btn edit"
                        title="Bu ortağın altına üye ekle"
                      >
                        <UserPlus size={13} />
                      </button>
                      <button
                        onClick={() => setEditCommission({
                          id: a.id,
                          username: a.username,
                          commission_type: a.commission_type || 'revshare',
                          revshare_percent: a.revshare_percent ?? '',
                          deposit_percent: a.deposit_percent ?? '',
                          cpa_amount: a.cpa_amount ?? '',
                          cpa_min_deposit: a.cpa_min_deposit ?? '',
                        })}
                        className="icon-btn edit"
                        title="Komisyon oranını düzenle"
                      >
                        <Percent size={13} />
                      </button>
                      <button onClick={() => setAdjust({
                        id: a.id,
                        username: a.username,
                        delta: '',
                        note: '',
                        direction: 'add',
                        balance: Number(a.balance || 0),
                      })}
                              className="icon-btn edit" title="Bakiye düzelt"><Plus size={13} /></button>
                      <button onClick={() => toggleStatus(a)} className={`icon-btn ${a.status === 'active' ? 'ban' : 'view'}`}
                              title={a.status === 'active' ? 'Askıya al' : 'Aktifleştir'}>
                        {a.status === 'active' ? <Ban size={13} /> : <CheckCircle2 size={13} />}
                      </button>
                      <button onClick={() => removeAffiliate(a)} className="icon-btn delete"
                              title="Ortaklığı tamamen kaldır">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-500">Affiliate kaydı yok</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto">
          <table className="admin-table w-full min-w-[900px]">
            <thead>
              <tr><th>TARİH</th><th>ORTAK</th><th>TUTAR</th><th>YÖNTEM</th><th>ÇEKİM HESABI</th><th>DURUM</th><th>NOT</th><th>İŞLEMLER</th></tr>
            </thead>
            <tbody>
              {withdraws.map((w) => (
                <tr key={w.id}>
                  <td className="text-slate-400 text-xs">{fmtDate(w.created_at)}</td>
                  <td className="font-semibold">{w.username}</td>
                  <td className="font-bold text-cyan-400">{money(w.amount)}</td>
                  <td className="text-slate-300">
                    {w.method === 'balance' ? 'Site Bakiyesi' : w.method === 'crypto' ? 'Kripto' : 'Banka'}
                  </td>
                  <td className="text-slate-400 text-xs">
                    {w.method === 'crypto' ? (
                      <>
                        <div>{w.crypto_asset || 'Kripto'} ({w.crypto_network || '-'})</div>
                        <div className="max-w-[220px] truncate" title={w.wallet_address || ''}>{w.wallet_address || '-'}</div>
                      </>
                    ) : w.method === 'balance' ? '-' : (
                      <>
                        <div>{w.bank_name || 'Banka'} · {w.iban || '-'}</div>
                        <div>{w.name || ''}</div>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`badge-pill ${w.status === 'onaylandi' ? 'badge-green' : w.status === 'reddedildi' ? 'badge-red' : 'badge-yellow'}`}>
                      {w.status}
                    </span>
                  </td>
                  <td className="text-slate-500 text-xs">{w.admin_note || '-'}</td>
                  <td>
                    {w.status === 'beklemede' && canPayout ? (
                      <div className="flex gap-1">
                        <button onClick={() => resolveWithdraw(w, 'onaylandi')} className="icon-btn view" title="Onayla"><CheckCircle2 size={13} /></button>
                        <button onClick={() => resolveWithdraw(w, 'reddedildi')} className="icon-btn ban" title="Reddet"><X size={13} /></button>
                      </div>
                    ) : (
                      <span className="text-slate-600 text-xs" title={w.status === 'beklemede' ? 'Ödeme kararı için finance.write yetkisi gerekli' : undefined}>-</span>
                    )}
                  </td>
                </tr>
              ))}
              {withdraws.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500">Çekim talebi yok</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Detay modalı */}
      {detail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">{detail.affiliate.username} · <span className="font-mono text-cyan-400">{detail.affiliate.referral_code}</span></h3>
              <button onClick={() => setDetail(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="admin-card p-3">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><TrendingUp size={11} /> Kazanç</div>
                  <div className="text-lg font-bold text-cyan-400 mt-1">{money(detail.affiliate.total_earned)}</div>
                </div>
                <div className="admin-card p-3">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Wallet size={11} /> Bakiye</div>
                  <div className="text-lg font-bold mt-1">{money(detail.affiliate.balance)}</div>
                </div>
                <div className="admin-card p-3">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><ArrowUpCircle size={11} /> Çekilen</div>
                  <div className="text-lg font-bold text-emerald-400 mt-1">{money(detail.affiliate.total_withdrawn)}</div>
                </div>
                <div className="admin-card p-3">
                  <div className="text-xs text-slate-400">Beklemede</div>
                  <div className="text-lg font-bold text-amber-400 mt-1">{money(detail.affiliate.pending_withdraw)}</div>
                </div>
              </div>

              <div className="mb-2 flex items-center gap-2">
                <div className="text-sm font-semibold">Referanslar ({detail.referrals.length})</div>
                <button
                  onClick={() => setAddMember({
                    id: detail.affiliate.id,
                    affiliateName: detail.affiliate.username,
                    username: '',
                  })}
                  className="admin-btn-secondary ml-auto flex items-center gap-1.5 text-xs"
                >
                  <UserPlus size={12} /> Manuel Üye Ekle
                </button>
              </div>
              <div className="overflow-x-auto mb-5">
                <table className="admin-table w-full min-w-[820px]">
                  <thead>
                    <tr>
                      <th>ÜYE</th><th>DURUM</th><th>YATIRIM</th><th>ÇEKİM</th>
                      <th>NET FARK</th><th>KOMİSYON</th><th>KAYIT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.referrals.map((r) => (
                      <tr key={r.id}>
                        <td className="font-semibold">{r.username}</td>
                        <td><span className={`badge-pill ${r.status === 'qualified' ? 'badge-green' : 'badge-gray'}`}>{r.status === 'qualified' ? 'Yatırım Yaptı' : 'Kayıtlı'}</span></td>
                        <td>{money(r.total_deposit)}</td>
                        <td className="text-red-400">{money(r.total_withdraw)}</td>
                        <td className={Number(r.commissioned_base || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'} title="Onaylı yatırım − onaylı çekim">
                          {money(r.commissioned_base)}
                        </td>
                        <td className={`font-bold ${Number(r.commission_earned || 0) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{money(r.commission_earned)}</td>
                        <td className="text-slate-500 text-xs">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                    {detail.referrals.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Referans yok</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="text-sm font-semibold mb-2">Komisyon Hareketleri ({detail.commissions.length})</div>
              <div className="overflow-x-auto">
                <table className="admin-table w-full min-w-[600px]">
                  <thead><tr><th>TARİH</th><th>TÜR</th><th>ÜYE</th><th>TABAN</th><th>ORAN</th><th>TUTAR</th><th>NOT</th><th>İŞLEM</th></tr></thead>
                  <tbody>
                    {detail.commissions.map((c) => (
                      <tr key={c.id}>
                        <td className="text-slate-400 text-xs">{fmtDate(c.created_at)}</td>
                        <td>{TYPE_LABELS[c.type] || c.type}</td>
                        <td className="text-slate-300">{c.referred_username || '-'}</td>
                        <td className="text-slate-400">{money(c.base_amount)}</td>
                        <td className="text-slate-400">{c.percent ? `%${c.percent}` : '-'}</td>
                        <td className={`font-bold ${Number(c.amount || 0) >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{money(c.amount)}</td>
                        <td className="text-slate-500 text-xs">{c.note || '-'}</td>
                        <td>
                          {/* Yalnızca manuel düzeltme geri alınabilir: revshare
                              satırları uzlaştırmayla yeniden hesaplanır. */}
                          {canManage && c.type === 'manual' && Number(c.amount || 0) !== 0 && (
                            <button onClick={() => revertCommission(c)} disabled={busy}
                                    className="icon-btn edit" title="Bu düzeltmeyi geri al">
                              <Undo2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {detail.commissions.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">Komisyon kaydı yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni ortak oluşturma */}
      {creating && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setCreating(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">Yeni Affiliate Ortağı</h3>
              <button onClick={() => setCreating(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[11px] text-slate-400 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
                Mevcut bir üyeyi ortak yapar: referans kodu üretilir ve affiliate
                yetkisi otomatik verilir. Komisyon modeli ve oranı aynı işlemde
                kaydedilir.
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Kullanıcı Adı *</label>
                <input value={creating.username}
                       onChange={(e) => setCreating({ ...creating, username: e.target.value })}
                       className="admin-input" placeholder="Ortak yapılacak üyenin kullanıcı adı" />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Ödeme Adı (opsiyonel)</label>
                <input value={creating.payout_name}
                       onChange={(e) => setCreating({ ...creating, payout_name: e.target.value })}
                       className="admin-input" placeholder="Hesap sahibi" />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">IBAN (opsiyonel)</label>
                <input value={creating.payout_iban}
                       onChange={(e) => setCreating({ ...creating, payout_iban: e.target.value })}
                       className="admin-input" placeholder="TR..." />
              </div>
              <CommissionFields form={creating} onChange={setCreating} />
              <div className="rounded-lg border border-cyan-400/25 bg-cyan-400/5 p-3 text-[11px] text-slate-400">
                Oran yalnızca bu ortak için geçerlidir. Değer 0 ise komisyon işlenmez.
              </div>
              <button disabled={busy} onClick={submitCreate} className="admin-btn-primary w-full py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Ortak Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Komisyon oranı düzenleme */}
      {editCommission && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setEditCommission(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">Komisyon Oranı · {editCommission.username}</h3>
              <button onClick={() => setEditCommission(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-3">
              <CommissionFields form={editCommission} onChange={setEditCommission} />
              <div className="rounded-lg border border-cyan-400/25 bg-cyan-400/5 p-3 text-[11px] text-slate-400">
                Kaydedilen oran bu ortağın yeni satışlarında hemen kullanılır.
                Gelir payı değişirse mevcut onaylı yatırım ve çekimler de yeni oranla uzlaştırılır.
              </div>
              <button disabled={busy} onClick={submitCommission} className="admin-btn-primary w-full py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Oranı Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ortağın altına manuel üye ekleme */}
      {addMember && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setAddMember(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">Üye Ekle · {addMember.affiliateName}</h3>
              <button onClick={() => setAddMember(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[11px] text-slate-400 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
                Referans linkiyle gelmemiş bir üyeyi elle bu ortağın altına bağlar.
                Bağlandığı andan itibaren üyenin yatırım ve kayıpları bu ortağa
                komisyon üretir; geçmiş hareketler geriye dönük hesaplanmaz.
                Bir üye aynı anda yalnızca tek bir ortağa bağlı olabilir.
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Kullanıcı Adı *</label>
                <input value={addMember.username}
                       onChange={(e) => setAddMember({ ...addMember, username: e.target.value })}
                       className="admin-input" placeholder="Bağlanacak üyenin kullanıcı adı" />
              </div>
              <button disabled={busy} onClick={submitAddMember} className="admin-btn-primary w-full py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Üyeyi Bağla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bakiye düzeltme modalı */}
      {adjust && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setAdjust(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">Bakiye Düzeltme · {adjust.username}</h3>
              <button onClick={() => setAdjust(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-3">
              {/* Yön ayrı bir seçimdir: eksi işareti yazdırmak telefonda
                  imkânsızdı (sayısal klavyede eksi tuşu yok) ve düşülmesi
                  istenen tutar sessizce ekleniyordu. */}
              <div>
                <label className="text-xs text-slate-400 block mb-1">İşlem Yönü</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'add', label: 'Bakiyeye Ekle', color: '16,185,129' },
                    { key: 'subtract', label: 'Bakiyeden Düş', color: '244,63,94' },
                  ].map((option) => {
                    const active = adjustDirection(adjust) === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setAdjust({ ...adjust, direction: option.key })}
                        className="py-2.5 rounded-lg text-sm font-semibold border transition"
                        style={{
                          background: active ? `rgba(${option.color},0.16)` : 'rgba(255,255,255,0.03)',
                          borderColor: active ? `rgba(${option.color},0.55)` : 'rgba(255,255,255,0.08)',
                          color: active ? `rgb(${option.color})` : '#94a3b8',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Tutar</label>
                <input type="number" min="0" step="0.01" inputMode="decimal" value={adjust.delta}
                       onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })}
                       className="admin-input" placeholder="0.00" />
              </div>
              {/* Kaydetmeden önce sonucu yazar: yön yanlış seçildiyse burada
                  görülür. */}
              {adjustOverdraft(adjust) ? (
                <div className="text-[11px] text-red-300 p-3 rounded-lg"
                     style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}>
                  Bakiye <span className="font-semibold">{money(adjust.balance)}</span> —
                  bundan fazlasını düşemezsiniz. Bakiye eksiye indirilmez;
                  en fazla sıfırlanır.
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 p-3 rounded-lg"
                     style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
                  Bakiye <span className="text-slate-200 font-semibold">{money(adjust.balance)}</span>
                  {' → '}
                  <span className={adjustDelta(adjust) < 0 ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold'}>
                    {money(Number(adjust.balance || 0) + adjustDelta(adjust))}
                  </span>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400 block mb-1">Not</label>
                <input value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })}
                       className="admin-input" placeholder="Düzeltme gerekçesi" />
              </div>
              <button disabled={busy || !adjustDelta(adjust) || adjustOverdraft(adjust)}
                      onClick={submitAdjust} className="admin-btn-primary w-full py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}
                {adjustDelta(adjust) < 0 ? 'Bakiyeden Düş' : 'Bakiyeye Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Affiliates;
